import { Link } from "@tanstack/react-router";
import type { Club, FeaturedClub, RecommendedClub } from "@/api/clubsApi";
import { Icon } from "@/components/ui/Icon";

interface FeaturedClubCardProps {
  club: Club | FeaturedClub | RecommendedClub;
  onDismiss?: (clubId: string) => void;
}

/**
 * Compact discover card matching mobile CompactClubCard layout:
 * - Square image on the left (104px, 16px radius)
 * - Club name + location in the middle
 * - Dismiss (X) button at top-right
 * - 300px wide, 24px border radius, 1px border, 8px padding
 */
export function FeaturedClubCard({ club, onDismiss }: FeaturedClubCardProps) {
  const isFeaturedClub = "club_id" in club;
  const clubId = isFeaturedClub ? club.club_id : (club.id || "");
  const clubName = isFeaturedClub ? club.club_name : club.name;
  const imageUrl = isFeaturedClub
    ? (club.primary_image_medium || club.primary_image_small || club.primary_image_full)
    : (club.primary_image_medium || club.primary_image_small || club.primary_image_full);
  const locationName = isFeaturedClub ? club.location_name : (club as Club).location_name;

  // Format location for compact display (mirrors mobile formatLocationStringForCompactDisplay)
  const displayLocation = locationName
    ? formatLocationForCompactDisplay(locationName)
    : null;

  return (
    <div className="relative w-[300px] border border-border-tertiary rounded-3xl p-2 shadow-[0px_2px_8px_0px_rgba(0,0,0,0.04)] transition-shadow hover:shadow-[0px_4px_24px_0px_rgba(0,0,0,0.08)]">
      <Link
        to="/clubs/$identifier"
        params={{ identifier: clubId }}
        className="flex flex-row items-center gap-3 pr-6"
      >
        {/* Square image */}
        <div className="w-[104px] h-[104px] shrink-0 rounded-2xl overflow-hidden relative">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={clubName}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-surface-2">
              <Icon name="globe" size={40} className="text-text-tertiary" />
            </div>
          )}
          {/* Inner border overlay */}
          <div className="absolute inset-0 rounded-2xl border border-border-tertiary pointer-events-none" />
        </div>

        {/* Text content */}
        <div className="flex-1 flex flex-col gap-1 min-w-0">
          <span className="text-h6 text-text-primary font-medium line-clamp-2">
            {clubName}
          </span>
          {displayLocation && (
            <span className="text-body-2 text-text-secondary line-clamp-2">
              {displayLocation}
            </span>
          )}
        </div>
      </Link>

      {/* Dismiss button */}
      {onDismiss && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onDismiss(clubId);
          }}
          className="absolute top-2 right-2 w-[26px] h-[26px] rounded-2xl flex items-center justify-center hover:bg-surface-2 transition-colors"
        >
          <Icon name="circle-xmark" size={20} className="text-text-disabled" />
        </button>
      )}
    </div>
  );
}

/** Mirrors mobile formatLocationStringForCompactDisplay */
function formatLocationForCompactDisplay(locationString: string): string {
  const match = locationString.match(/^([^,]+),\s*([^,]+)(?:,\s*[^,]+)?$/);
  if (match) {
    const [, specific, city] = match;
    if (specific.trim() !== city.trim()) {
      return `${specific.trim()}, ${city.trim()}`;
    }
  }
  return locationString;
}
